import { z } from 'zod';
/** Site token: 32 bajtov v hex (64 znakov) — presne to, čo generuje `openssl rand -hex 32`. */
export const SITE_TOKEN_MIN_LENGTH = 32;
export const SiteTokenSchema = z
    .string()
    .trim()
    .min(SITE_TOKEN_MIN_LENGTH, `token musí mať aspoň ${SITE_TOKEN_MIN_LENGTH} znakov`)
    .max(256)
    .regex(/^[^\s'"\\]+$/, 'token nesmie obsahovať medzery ani úvodzovky');
/** URL sledovanej stránky — len http(s), bez používateľa a hesla. Ďalšie kontroly (SSRF) robí backend. */
export const SiteUrlSchema = z
    .string()
    .trim()
    .url()
    .max(2048)
    .refine((u) => /^https?:\/\//i.test(u), 'URL musí začínať http:// alebo https://')
    .refine((u) => !/^https?:\/\/[^/]*@/i.test(u), 'URL nesmie obsahovať používateľa a heslo');
export const SiteNameSchema = z.string().trim().min(1, 'názov je povinný').max(120);
export const VerifySiteSchema = z.object({
    url: SiteUrlSchema,
    token: SiteTokenSchema,
});
export const CreateSiteSchema = z.object({
    name: SiteNameSchema,
    url: SiteUrlSchema,
    token: SiteTokenSchema,
});
/**
 * Prečo overenie zlyhalo — nie len „chyba". Každý dôvod má inú nápravu:
 * zlý token → wp-config; missing_plugin → nahrať súbor; cached → vylúčiť
 * /wp-json/ametica z cache; blocked_url → URL mieri do internej siete.
 */
export const VerifyFailureReasonSchema = z.enum([
    'blocked_url',
    'unreachable',
    'timeout',
    'too_many_redirects',
    'unauthorized',
    'missing_plugin',
    'cached',
    'invalid_payload',
    'http_error',
]);
export const VerifyResultSchema = z.discriminatedUnion('ok', [
    z.object({
        ok: z.literal(true),
        wpVersion: z.string(),
        phpVersion: z.string(),
        plugins: z.number().int(),
        themes: z.number().int(),
        inventoryComplete: z.boolean(),
        warnings: z.array(z.string()),
    }),
    z.object({
        ok: z.literal(false),
        reason: VerifyFailureReasonSchema,
        message: z.string(),
        /** HTTP status vzdialenej stránky, ak nejaký bol */
        status: z.number().int().optional(),
    }),
]);
export const SiteReportStatusSchema = z.enum(['NEVER', 'OK', 'ERROR']);
/** Riadok v zozname stránok. Token sa tu NIKDY neobjaví. */
export const SiteSummarySchema = z.object({
    id: z.string(),
    name: z.string(),
    url: z.string(),
    host: z.string(),
    createdAt: z.string(),
    lastReportAt: z.string().nullable(),
    lastReportStatus: SiteReportStatusSchema,
    lastReportError: z.string().nullable(),
    inventoryComplete: z.boolean(),
    /** Stav pripojenia odvodený z reportu: ok / stale (report starší než 24 h) / error / never */
    connection: z.enum(['ok', 'stale', 'error', 'never']),
    findings: z.object({
        open: z.number().int(),
        worst: z.enum(['critical', 'high', 'medium', 'low', 'unconfirmed']).nullable(),
    }),
});
/** Riadok do wp-config.php — jediné miesto, kde sa jeho tvar definuje (UI aj README). */
export const wpConfigLine = (token) => `define('AMETICA_SECURITY_TOKEN', '${token}');`;
export const REPORT_PATH = '/wp-json/ametica/v1/report';
